<template>
  <div class="content-selector">
    <div v-if="showContentTypeSelect" class="content-type-select">
      <label>콘텐츠 타입</label>
      <select v-model="selectedType" @change="onContentTypeChange">
        <option value="">-- 타입 선택 --</option>
        <option
          v-for="option in contentTypeOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="content-selector-wrapper">
      <template v-if="selectedType === ARTICLE_TYPES.ARTIST">
        <SelectArtistUserAccountId
          v-model:userAccountId="selectedContentId"
        />
      </template>

      <template v-else-if="selectedType === ARTICLE_TYPES.NEWS">
        <SelectNews v-model:contentId="selectedContentId" />
      </template>

      <template v-else-if="selectedType === ARTICLE_TYPES.SPECIAL">
        <SelectSpecial v-model:contentId="selectedContentId" />
      </template>

      <template v-else-if="selectedType === ARTICLE_TYPES.EVENT">
        <SelectEvent v-model:contentId="selectedContentId" />
      </template>

      <template v-else-if="selectedType === ARTICLE_TYPES.EXHIBITION">
        <SelectExhibition v-model:contentId="selectedContentId" />
      </template>

      <template v-else-if="selectedType === ARTICLE_TYPES.ARTWORK">
        <SelectArtwork v-model:contentId="selectedContentId" />
      </template>

      <template v-else>
        <p style="color: #999; font-size: 12px;">타입을 먼저 선택해주세요.</p>
      </template>
    </div>

    <!-- Display selected content info -->
    <div v-if="selectedContentId" class="selected-content-info">
      <span class="badge" :style="{ backgroundColor: contentTypeColor }">
        {{ contentTypeLabel }}
      </span>
      <span class="content-id">ID: {{ selectedContentId }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref } from 'vue';
import { ARTICLE_TYPES, getArticleTypeLabel, getArticleTypeColor, getArticleTypeOptions } from '@/constants/articleType';
import SelectArtistUserAccountId from '@/components/ui/SelectArtistUserAccountId.vue';
import SelectNews from '@/components/ui/SelectNews.vue';
import SelectSpecial from '@/components/ui/SelectSpecial.vue';
import SelectEvent from '@/components/ui/SelectEvent.vue';
import SelectExhibition from '@/components/ui/SelectExhibition.vue';
import SelectArtwork from '@/components/ui/SelectArtwork.vue';

const props = defineProps({
  contentType: {
    type: String,
    default: '',
  },
  contentId: {
    type: [String, Number],
    default: '',
  },
  showContentTypeSelect: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:contentType', 'update:contentId']);

const selectedType = computed({
  get: () => props.contentType,
  set: (value) => emit('update:contentType', value),
});

const selectedContentId = computed({
  get: () => props.contentId,
  set: (value) => emit('update:contentId', value),
});

const contentTypeOptions = computed(() => getArticleTypeOptions());

const contentTypeLabel = computed(() => getArticleTypeLabel(selectedType.value));
const contentTypeColor = computed(() => getArticleTypeColor(selectedType.value));

const onContentTypeChange = () => {
  if (selectedType.value) {
    selectedContentId.value = '';
  }
};
</script>

<style scoped>
.content-selector {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.content-type-select {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.content-type-select label {
  font-weight: 600;
  font-size: 12px;
  color: #333;
}

.content-type-select select {
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  background-color: #fff;
}

.content-type-select select:hover {
  border-color: #999;
}

.content-selector-wrapper {
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #eee;
}

.selected-content-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: #f0f0f0;
  border-radius: 4px;
  font-size: 12px;
}

.badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 3px;
  color: white;
  font-weight: 600;
  font-size: 11px;
}

.content-id {
  color: #666;
}
</style>
